import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Alert,
  Snackbar,
  TextField,
  Button,
  Link,
  Stack,
  Grid
} from '@mui/material';
import GoogleSignInButton from './GoogleSignInButton';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Enhanced Login Page Component
 * Provides a modern split-screen layout with Google authentication on the left
 * and marketing/feature information on the right
 */
const LoginPage = () => {
  const navigate = useNavigate();
  const { signInWithPassword } = useAuth();
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Form state for demo signup
  const [demoForm, setDemoForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    companyName: ''
  });
  
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  
  // State for username/password login fields
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });
  
  // Handle login form changes
  const handleLoginFormChange = (e) => {
    const { name, value } = e.target;
    setLoginForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle login form submission
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const { data, error: authError } = await signInWithPassword(
        loginForm.username, 
        loginForm.password
      );
      
      if (authError) {
        setError(authError.message || 'Login failed. Please check your credentials.');
        return;
      }
      
      if (data?.session) {
        console.log('Login successful, redirecting to main app...');
        // Manually redirect to the main app
        navigate('/');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('An unexpected error occurred. Please try again.');
    }
  };
  
  const handleLoginSuccess = () => {
    setShowSuccess(true);
    // Most likely the browser will redirect before this is visible
  };
  
  const handleLoginError = (error) => {
    setError(error.message || 'Authentication failed. Please try again.');
  };

  // Handle demo form input changes
  const handleDemoFormChange = (e) => {
    const { name, value } = e.target;
    setDemoForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Force recalculation of layout after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Form validation function
  const validateForm = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return (
      demoForm.firstName.trim() !== '' &&
      demoForm.lastName.trim() !== '' &&
      emailRegex.test(demoForm.email) &&
      demoForm.companyName.trim() !== ''
    );
  };

  // Handle demo form submission with HubSpot integration
  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);

    const hubspotData = {
      fields: [
        {
          objectTypeId: '0-1',
          name: 'firstname',
          value: demoForm.firstName
        },
        {
          objectTypeId: '0-1',
          name: 'lastname',
          value: demoForm.lastName
        },
        {
          objectTypeId: '0-1',
          name: 'email',
          value: demoForm.email
        },
        {
          objectTypeId: '0-1',
          name: 'company',
          value: demoForm.companyName
        }
      ],
      context: {
        pageUri: window.location.href,
        pageName: 'Wyspur Demo Request'
      }
    };

    try {
      const response = await fetch(
        'https://api.hsforms.com/submissions/v3/integration/submit/43646484/e606f57e-6380-4092-8395-db2bacc612d3',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(hubspotData)
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        setFormSubmitted(true);
        // Reset form
        setTimeout(() => {
          setDemoForm({
            email: '',
            firstName: '',
            lastName: '',
            companyName: ''
          });
          setFormSubmitted(false);
          setSubmitStatus(null);
        }, 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('HubSpot form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Define Wyspur colors directly to ensure consistency across environments
  const wyspurColors = {
    darkNavy: '#0D0D21',
    navy: '#161C3E',
    blue: '#2F6BFD',
    lightBlue: '#40C4FF',
    purple: '#9C27B0',
    yellow: '#FFD600',
    pink: '#FF4081',
    text: '#FFFFFF',
    border: 'rgba(255,255,255,0.12)',
    subtitle: '#FFFFFF'
  };

  return (
    <Box sx={{
      display: 'flex', 
      flexDirection: 'column',
      minHeight: '100vh', 
      width: '100%', 
      bgcolor: wyspurColors.darkNavy,
      position: 'relative'
    }}>
      {/* Header for logo and future buttons */}
      <Box sx={{
        width: '100%',
        height: '80px',
        display: 'flex',
        alignItems: 'center',
        px: 3,
        mb: 2,
        position: 'sticky', 
        top: 0, 
        zIndex: 10, 
        bgcolor: wyspurColors.darkNavy, 
      }}>
        <Box sx={{ maxWidth: '150px' }}>
          <img 
            src="/wyspur.png" 
            alt="Wyspur Logo" 
            style={{ width: '100%', height: 'auto' }} 
          />
        </Box>
        
        {/* Space for future buttons/navigation */}
        <Box sx={{ flex: 1 }} />
      </Box>

      {/* Main content - split layout with single page scroll */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        width: '100%',
        /* No fixed height to allow content to flow naturally */
        /* No overflow setting - inherit from parent */
      }}>
        {/* LEFT PANEL - Authentication */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: '40%' },
          p: { xs: 3, md: 6 },
          pt: { xs: 4, md: 6 }, /* Extra top padding to prevent header overlap */
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}>
          {/* Login Section */}
          <Paper elevation={3} sx={{
            bgcolor: wyspurColors.navy,
            borderRadius: 2,
            p: 3,
            position: 'relative',
            zIndex: 3,
            width: '80%', 
            maxWidth: '400px', 
            mx: 'auto', 
            backdropFilter: 'blur(10px)'
          }}>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography 
                variant="h5" 
                component="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  color: wyspurColors.text,
                  textAlign: 'center',
                  display: 'inline-flex',
                  alignItems: 'baseline',
                  justifyContent: 'center',
                  width: '100%'
                }}
              >
                wyspur<Typography component="span" sx={{ fontWeight: 700, color: wyspurColors.blue }}>AI</Typography>
              </Typography>
              <Box sx={{ mx: 'auto', textAlign: 'center', position: 'relative', zIndex: 3, maxWidth: '600px', px: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: wyspurColors.text }}>
                  Your expert business development assistant
                </Typography>
              </Box>
            </Box>
            
            {error && (
              <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            
            {/* Username/password fields */}
            <Box component="form" onSubmit={handleLoginSubmit} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={loginForm.username}
                onChange={handleLoginFormChange}
                margin="normal"
                variant="filled"
                InputLabelProps={{ sx: { color: wyspurColors.text } }}
                InputProps={{ sx: { color: wyspurColors.text } }}
              />
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={loginForm.password}
                onChange={handleLoginFormChange}
                margin="normal"
                variant="filled"
                InputLabelProps={{ sx: { color: wyspurColors.text } }}
                InputProps={{ sx: { color: wyspurColors.text } }}
              />
              <Button
                fullWidth
                type="submit"
                variant="contained"
                sx={{ 
                  mt: 2, 
                  mb: 2,
                  bgcolor: wyspurColors.blue,
                  '&:hover': { bgcolor: `${wyspurColors.blue}dd` }
                }}
              >
                Sign In
              </Button>
            </Box>
            
            <Typography variant="body2" align="center" sx={{ mb: 2, color: wyspurColors.text }}>
              or
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <GoogleSignInButton 
                onSuccess={handleLoginSuccess}
                onError={handleLoginError}
              />
            </Box>
            
            <Typography variant="body2" sx={{ color: wyspurColors.text }} align="center">
              By signing in, you agree to our{' '}
              <Link href="#" color="primary">Terms of Service</Link> and{' '}
              <Link href="#" color="primary">Privacy Policy</Link>
            </Typography>
          </Paper>
          
          {/* Feature cards section with guaranteed equal sizing */}

        </Box>

        {/* RIGHT PANEL - Information/Marketing */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: '60%' },
          p: { xs: 3, md: 4 },
          pt: { xs: 4, md: 2 }, /* Reduced top padding to eliminate excessive space */
          alignItems: 'center',
          justifyContent: 'flex-start', /* Align content to start at top */
          position: 'relative',
          boxSizing: 'border-box', /* Ensure padding doesn't expand width */
          maxWidth: { md: 'calc(100% - 32px)' }, /* Maintain constraint to align with left panel */
        }}>
          {/* Main heading */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h2" 
              component="h2" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                color: wyspurColors.text,
              }}
            >
              Outbound Sales on <Typography component="span" sx={{ color: wyspurColors.blue, fontSize: 'inherit', fontWeight: 'inherit' }}>Easy-Mode</Typography>
            </Typography>
            <Typography variant="h6" sx={{ 
              maxWidth: '100%', 
              color: wyspurColors.text, 
              mb: 4,
              textAlign: 'center',
              whiteSpace: 'nowrap'
            }}>
              Automate your prospect research for precision targeting in 3 easy steps
            </Typography>
            
            {/* Three numbered steps */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              gap: 4,
              mb: 4,
              width: '100%',
              maxWidth: '800px',
              justifyContent: 'center'
            }}>
              {/* Step 1 */}
              <Box sx={{ 
                flex: 1,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  bgcolor: wyspurColors.blue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>1</Typography>
                </Box>
                <Typography variant="h6" sx={{ color: wyspurColors.text, fontWeight: 600, mb: 1 }}>
                  Upload Your Prospect List
                </Typography>
                <Typography variant="body2" sx={{ color: wyspurColors.text, opacity: 0.8 }}>
                  Upload a prospect list from LinkedIn, Apollo, ZoomInfo, Salesforce or elsewhere
                </Typography>
              </Box>
              
              {/* Step 2 */}
              <Box sx={{ 
                flex: 1,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  bgcolor: wyspurColors.blue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>2</Typography>
                </Box>
                <Typography variant="h6" sx={{ color: wyspurColors.text, fontWeight: 600, mb: 1 }}>
                  Define Your Criteria
                </Typography>
                <Typography variant="body2" sx={{ color: wyspurColors.text, opacity: 0.8 }}>
                  Tell Wyspur AI what to look for on each prospect’s website to confirm they match your Ideal Client Profile
                </Typography>
              </Box>
              
              {/* Step 3 */}
              <Box sx={{ 
                flex: 1,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  bgcolor: wyspurColors.blue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>3</Typography>
                </Box>
                <Typography variant="h6" sx={{ color: wyspurColors.text, fontWeight: 600, mb: 1 }}>
                  Review & Export
                </Typography>
                <Typography variant="body2" sx={{ color: wyspurColors.text, opacity: 0.8 }}>
                  Review and export the enriched prospect list
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Demo video section - temporarily commented out */}

          {/* Demo sign-up form with width matching video */}
          <Box sx={{ width: '100%', maxWidth: '550px', display: 'flex', justifyContent: 'center' }}>
            <Paper 
              component="form" 
              onSubmit={handleDemoSubmit}
              elevation={3} 
              sx={{ 
                p: 3, 
                width: '100%',
                bgcolor: 'rgba(22, 28, 62, 0.8)', 
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                border: '1px solid rgba(60, 60, 90, 0.5)',
                boxSizing: 'border-box'
              }}
            >
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: wyspurColors.text }}>
              Request a personalized demo
            </Typography>
            
            {formSubmitted ? (
              <Alert severity="success" sx={{ mt: 2 }}>
                Thank you! We'll be in touch soon to schedule your demo.
              </Alert>
            ) : (
              <Stack spacing={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={demoForm.firstName}
                      onChange={handleDemoFormChange}
                      required
                      variant="filled"
                      InputLabelProps={{ sx: { color: wyspurColors.text } }}
                      InputProps={{ sx: { color: wyspurColors.text } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={demoForm.lastName}
                      onChange={handleDemoFormChange}
                      required
                      variant="filled"
                      InputLabelProps={{ sx: { color: wyspurColors.text } }}
                      InputProps={{ sx: { color: wyspurColors.text } }}
                    />
                  </Grid>
                </Grid>
                
                <TextField
                  fullWidth
                  label="Work Email"
                  name="email"
                  type="email"
                  value={demoForm.email}
                  onChange={handleDemoFormChange}
                  required
                  variant="filled"
                  InputLabelProps={{ sx: { color: wyspurColors.text } }}
                  InputProps={{ sx: { color: wyspurColors.text } }}
                />
                
                <TextField
                  fullWidth
                  label="Company Name"
                  name="companyName"
                  value={demoForm.companyName}
                  onChange={handleDemoFormChange}
                  required
                  variant="filled"
                  InputLabelProps={{ sx: { color: wyspurColors.text } }}
                  InputProps={{ sx: { color: wyspurColors.text } }}
                />
                
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  size="large" 
                  endIcon={!isSubmitting && <ArrowForwardIcon />}
                  disabled={isSubmitting}
                  sx={{ 
                    mt: 2, 
                    py: 1.5,
                    fontWeight: 600,
                    bgcolor: wyspurColors.blue,
                    '&:hover': { bgcolor: `${wyspurColors.blue}dd` }
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Request Demo'}
                </Button>
                
                {/* Success/Error messages */}
                {submitStatus === 'error' && !formSubmitted && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    Something went wrong. Please try again or contact support.
                  </Alert>
                )}
              </Stack>
            )}
            </Paper>
          </Box>
        </Box>
      </Box>
      
      {/* Add a small footer spacer to ensure content isn't cut off */}
      <Box sx={{ height: '20px', width: '100%' }} />
      
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success">
          Login successful. Redirecting...
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LoginPage;
